import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createOurExpertise,
  getAllActiveOurExpertises,
  getAllOurExpertises,
  getOurExpertiseByID,
  updateOurExpertise,
} from "./services/ourExpertise.services";
import { clearOurExpertiseDetails } from "./ourExpertiseSlice";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateOurExpertise: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const OurExpertiseDetail = useAppSelector(
    (state) => state.ourExpertise.OurExpertiseDetail
  );
  const OurExpertiseID = useAppSelector((state) => state.muidrawer.data);
  const formLoading = useAppSelector((state) => state.ourExpertise.formLoading);
  const isIndirect = useAppSelector((state) => state.muidrawer.isIndirect);
  const [formKey, setFormKey] = React.useState(0);

  useEffect(() => {
    if (OurExpertiseID) {
      dispatch(getOurExpertiseByID(OurExpertiseID));
    }
  }, [OurExpertiseID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [OurExpertiseDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearOurExpertiseDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const ExpertiseImage =
      values?.ExpertiseImage &&
      typeof values?.ExpertiseImage !== "string" &&
      Array.isArray(values?.ExpertiseImage) &&
      values?.ExpertiseImage?.length > 0
        ? values.ExpertiseImage[0].getRawFile()
        : "";

    try {
      if (!OurExpertiseID) {
        ImageData.append("ID", "0");
        ImageData.append("ExpertiseImage", ExpertiseImage);
        ImageData.append("Title", values?.Title);
        ImageData.append("Description", values?.Description);
        ImageData.append("IsActive", values?.IsActive === 1 ? "true" : "false");

        const response = await dispatch(createOurExpertise(ImageData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearOurExpertiseDetails());
          if (isIndirect) {
            dispatch(getAllActiveOurExpertises());
          } else {
            dispatch(getAllOurExpertises());
          }
        }
      } else {
        ImageData.append("ID", OurExpertiseID);
        ImageData.append("ExpertiseImage", ExpertiseImage);
        ImageData.append("Title", values?.Title);
        ImageData.append("Description", values?.Description);
        ImageData.append("IsActive", values?.IsActive === 1 ? "true" : "false");

        const response = await dispatch(updateOurExpertise(ImageData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearOurExpertiseDetails());
          dispatch(getAllOurExpertises());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={OurExpertiseDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {OurExpertiseID ? "Update Expertise" : "Add Expertise"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </IconButton>
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="Title"
                  label="Title"
                  placeholder="Title"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Description"
                  label="Description"
                  placeholder="Description"
                  component={FormTextArea}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  justifyContent="start"
                  name={`ExpertiseImage`}
                  label="Expertise Image"
                  type="file"
                  accept="image/*"
                  alternateName="Expertise Image"
                  component={FormImageUploadMorden}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                style={{
                  marginTop: "20px",
                }}
              >
                <ButtonWithLoading
                  label={OurExpertiseID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateOurExpertise;
